import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from "../../config";

// This form used to ask for eleven fields: username, email, two passwords,
// first and last name, street, city, state, zip and phone. Six of those were
// required and read by nothing -- the server never sent them to Stripe and
// never used them for billing or tax. What it now asks for is what the account
// actually needs, plus the terms acceptance the backend records against a
// version.
//
// Billing address is not collected here on purpose: Stripe gathers it on the
// PaymentMethod when the card is entered, which is both authoritative and one
// fewer thing to ask twice and then have disagree.

export default function RegisterCustomer() {
    const [form, setForm] = useState({
        display_name: '',
        email: '',
        password: '',
        password2: '',
    });
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');

        if (form.password !== form.password2) {
            setError('Those passwords do not match.');
            return;
        }
        if (!acceptTerms) {
            setError('Please accept the terms to continue.');
            return;
        }

        setSubmitting(true);
        try {
            const res = await fetch(`${API_BASE_URL}/register-customer`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, accept_terms: true }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.message || 'Registration failed.');
                setSubmitting(false);
                return;
            }
            setDone(true);
        } catch {
            setError('Network error. Your account was not created.');
        }
        setSubmitting(false);
    }

    if (done) {
        return (
            <div className="wb-page wb-page--narrow">
                <h1>Check your email</h1>
                <p className="wb-sub">
                    We sent a confirmation link to <strong>{form.email}</strong>. Open it to
                    finish setting up your account.
                </p>
                <button className="wb-btn wb-btn--primary" onClick={() => navigate('/login')}>
                    Go to sign in
                </button>
            </div>
        );
    }

    return (
        <div className="wb-page wb-page--narrow">
            <h1>Advertise on WallBurst</h1>
            <p className="wb-sub" style={{ marginBottom: '1.5rem' }}>
                Create an account to browse screens and book time. Payment details come
                later, at checkout.
            </p>

            <form onSubmit={handleSubmit} className="wb-card">
                <label className="wb-field">
                    <span className="wb-label">Your name</span>
                    <input
                        className="wb-input" name="display_name" autoComplete="name"
                        value={form.display_name} onChange={handleChange} required
                    />
                </label>

                <label className="wb-field">
                    <span className="wb-label">Email</span>
                    <input
                        className="wb-input" name="email" type="email" autoComplete="email"
                        value={form.email} onChange={handleChange} required
                    />
                    <span className="wb-hint">This is what you will sign in with.</span>
                </label>

                <label className="wb-field">
                    <span className="wb-label">Password</span>
                    <input
                        className="wb-input" name="password" type="password"
                        autoComplete="new-password"
                        value={form.password} onChange={handleChange} required
                    />
                    <span className="wb-hint">
                        At least 8 characters, with an upper and lower case letter, a number,
                        and one of @$!%*?&amp;
                    </span>
                </label>

                <label className="wb-field">
                    <span className="wb-label">Confirm password</span>
                    <input
                        className="wb-input" name="password2" type="password"
                        autoComplete="new-password"
                        value={form.password2} onChange={handleChange} required
                    />
                </label>

                <label className="wb-row" style={{ marginBottom: '1rem', alignItems: 'flex-start' }}>
                    <input
                        type="checkbox" checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                    />
                    <span style={{ flex: 1 }}>
                        I accept the <Link to="/terms">Terms of Service</Link> and{" "}
                        <Link to="/privacy">Privacy Policy</Link>.
                    </span>
                </label>

                <button
                    type="submit"
                    className="wb-btn wb-btn--primary wb-btn--block"
                    disabled={submitting}
                >
                    {submitting ? 'Creating account…' : 'Create account'}
                </button>

                {error && <div className="wb-note wb-note--danger" style={{ marginTop: '1rem' }}>{error}</div>}
            </form>

            <p className="wb-sub" style={{ marginTop: '1.5rem' }}>
                Own a screen instead? <Link to="/register/vendor">List it here</Link>.
                Already have an account? <Link to="/login">Sign in</Link>.
            </p>
        </div>
    );
}
